import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  Building2, Search, Bell, Calendar, FileText, Clock,
  MapPin, ChevronRight, Filter, AlertCircle, CheckCircle2,
  Download, RefreshCw, ExternalLink
} from 'lucide-react';

const courtStats = [
  { label: 'Courts Tracked', value: '19,000+', icon: Building2, color: 'blue' },
  { label: 'Cases Monitored', value: '24', icon: FileText, color: 'green' },
  { label: 'Updates Today', value: '8', icon: Bell, color: 'yellow' },
  { label: 'Upcoming Hearings', value: '12', icon: Calendar, color: 'purple' },
];

const recentUpdates = [
  { id: 1, case: 'WP/1234/2024', court: 'Delhi High Court', type: 'Order Uploaded', time: '10 mins ago', priority: 'high' },
  { id: 2, case: 'CS/567/2024', court: 'Tis Hazari Court', type: 'Next Date: 25 Jan', time: '1 hour ago', priority: 'medium' },
  { id: 3, case: 'CRL/890/2024', court: 'Saket Court', type: 'Cause List Published', time: '3 hours ago', priority: 'low' },
  { id: 4, case: 'ARB/123/2024', court: 'DIAC', type: 'Hearing Concluded', time: '5 hours ago', priority: 'medium' },
];

const causelist = [
  { id: 1, slNo: 1, caseNo: 'WP/1234/2024', parties: 'Sharma vs State', item: 'Main', bench: 'Division Bench', time: '10:30 AM' },
  { id: 2, slNo: 5, caseNo: 'CS/567/2024', parties: 'Verma vs ABC Ltd', item: 'IA', bench: 'Single Judge', time: '11:00 AM' },
  { id: 3, slNo: 12, caseNo: 'WP/789/2024', parties: 'XYZ vs Union of India', item: 'Main', bench: 'Division Bench', time: '2:00 PM' },
];

const Courts = () => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('updates');
  const [selectedCourt, setSelectedCourt] = useState('all');

  return (
    <MainLayout title="Court Tracking" subtitle="Real-time updates from 19,000+ Indian courts">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {courtStats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02, y: -5 }}
            className="glass-card p-6 relative overflow-hidden"
          >
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-${stat.color}-500 to-${stat.color}-400`} />
            <motion.div
              className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-${stat.color}-500/20 blur-2xl`}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <h3 className="text-3xl font-bold mt-1">{stat.value}</h3>
              </div>
              <div className={`p-3 rounded-xl bg-${stat.color}-500/20`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-500`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1 p-1 rounded-xl glass-card">
          {['updates', 'causelist', 'calendar', 'alerts'].map((tab) => (
            <motion.button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
                activeTab === tab ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white' : ''
              }`}
              whileTap={{ scale: 0.95 }}
            >
              {tab === 'causelist' ? 'Cause List' : tab}
            </motion.button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <select
            value={selectedCourt}
            onChange={(e) => setSelectedCourt(e.target.value)}
            className="px-4 py-3 rounded-xl glass-card bg-transparent outline-none"
          >
            <option value="all">All Courts</option>
            <option value="sc">Supreme Court</option>
            <option value="hc">High Courts</option>
            <option value="district">District Courts</option>
            <option value="tribunal">Tribunals</option>
          </select>
          <motion.button
            className="flex items-center gap-2 px-4 py-3 rounded-xl glass-card"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RefreshCw className="w-5 h-5" />
            <span>Refresh</span>
          </motion.button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'updates' && (
          <motion.div
            key="updates"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-3 gap-6"
          >
            {/* Updates List */}
            <div className="col-span-2">
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold">Recent Updates</h3>
                  <button className="text-sm text-blue-500">View All</button>
                </div>
                <div className="space-y-4">
                  {recentUpdates.map((update, index) => (
                    <motion.div
                      key={update.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.01, x: 5 }}
                      className="p-4 rounded-xl cursor-pointer transition-all"
                      style={{
                        background: update.priority === 'high' 
                          ? isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)'
                          : isDark ? 'rgba(45, 140, 255, 0.1)' : 'rgba(45, 140, 255, 0.05)',
                        border: `1px solid ${update.priority === 'high' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(45, 140, 255, 0.1)'}`
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-xl ${
                            update.priority === 'high' ? 'bg-red-500/20' : 'bg-blue-500/20'
                          }`}>
                            {update.type.includes('Order') ? (
                              <FileText className={`w-5 h-5 ${update.priority === 'high' ? 'text-red-500' : 'text-blue-500'}`} />
                            ) : update.type.includes('Date') ? (
                              <Calendar className={`w-5 h-5 ${update.priority === 'high' ? 'text-red-500' : 'text-blue-500'}`} />
                            ) : (
                              <Bell className={`w-5 h-5 ${update.priority === 'high' ? 'text-red-500' : 'text-blue-500'}`} />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold">{update.case}</p>
                            <p className="text-sm text-gray-500">{update.court}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-medium ${
                            update.priority === 'high' ? 'text-red-500' : 'text-blue-500'
                          }`}>{update.type}</p>
                          <p className="text-sm text-gray-500">{update.time}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-6">
              {/* Add Case Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6"
              >
                <h3 className="font-semibold mb-4">Track New Case</h3>
                <input
                  type="text"
                  placeholder="Enter CNR Number"
                  className="w-full mb-3 input-glass"
                />
                <motion.button
                  className="w-full py-3 rounded-xl btn-primary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Add to Tracking
                </motion.button>
              </motion.div>

              {/* Court Status */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-card p-6"
              >
                <h3 className="font-semibold mb-4">Court Status</h3>
                <div className="space-y-3">
                  {[
                    { name: 'Supreme Court', status: 'Working', color: 'green' },
                    { name: 'Delhi High Court', status: 'Working', color: 'green' },
                    { name: 'Bombay High Court', status: 'Holiday', color: 'red' },
                  ].map((court) => (
                    <div key={court.name} className="flex items-center justify-between p-3 rounded-xl bg-gray-500/5">
                      <span className="text-sm">{court.name}</span>
                      <span className={`text-xs px-2 py-1 rounded-full bg-${court.color}-500/20 text-${court.color}-500`}>
                        {court.status}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {activeTab === 'causelist' && (
          <motion.div
            key="causelist"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="glass-card overflow-hidden"
          >
            <div className="p-6 border-b border-gray-500/20">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Today's Cause List</h3>
                  <p className="text-sm text-gray-500">Delhi High Court - 15 January 2025</p>
                </div>
                <motion.button
                  className="flex items-center gap-2 px-4 py-2 rounded-xl glass-card"
                  whileHover={{ scale: 1.02 }}
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </motion.button>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8B] text-white">
                  <th className="px-6 py-4 text-left">Sl. No</th>
                  <th className="px-6 py-4 text-left">Case No.</th>
                  <th className="px-6 py-4 text-left">Parties</th>
                  <th className="px-6 py-4 text-left">Item</th>
                  <th className="px-6 py-4 text-left">Bench</th>
                  <th className="px-6 py-4 text-left">Time</th>
                  <th className="px-6 py-4 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {causelist.map((item, index) => (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="border-b border-gray-500/10 hover:bg-blue-500/5"
                  >
                    <td className="px-6 py-4">{item.slNo}</td>
                    <td className="px-6 py-4 font-semibold text-blue-500">{item.caseNo}</td>
                    <td className="px-6 py-4">{item.parties}</td>
                    <td className="px-6 py-4">{item.item}</td>
                    <td className="px-6 py-4">{item.bench}</td>
                    <td className="px-6 py-4 font-semibold">{item.time}</td>
                    <td className="px-6 py-4">
                      <motion.button
                        className="text-blue-500 flex items-center gap-1"
                        whileHover={{ x: 3 }}
                      >
                        View <ExternalLink className="w-4 h-4" />
                      </motion.button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        )}

        {activeTab === 'calendar' && (
          <motion.div
            key="calendar"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="glass-card p-6"
          >
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-blue-500" />
              <h3 className="text-xl font-semibold mb-2">Court Calendar</h3>
              <p className="text-gray-500">Interactive calendar view coming soon</p>
            </div>
          </motion.div>
        )}

        {activeTab === 'alerts' && (
          <motion.div
            key="alerts"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="glass-card p-6"
          >
            <h3 className="text-lg font-semibold mb-6">Alert Settings</h3>
            <div className="space-y-4">
              {[
                { label: 'Order Uploads', desc: 'Get notified when new orders are uploaded', enabled: true },
                { label: 'Next Date Updates', desc: 'Receive alerts for date changes', enabled: true },
                { label: 'Cause List Publication', desc: 'Daily cause list notifications', enabled: false },
                { label: 'WhatsApp Alerts', desc: 'Receive updates on WhatsApp', enabled: true },
              ].map((alert) => (
                <div key={alert.label} className="flex items-center justify-between p-4 rounded-xl bg-gray-500/5">
                  <div>
                    <p className="font-medium">{alert.label}</p>
                    <p className="text-sm text-gray-500">{alert.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked={alert.enabled} className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-500/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                  </label>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </MainLayout>
  );
};

export default Courts;
